/// <amd-module name="Test/Analytics/Widgets/DetailsWidget/DetailsWidget" />
import { AnalyticsUnavailableMessage } from "Analytics/Shared/Visuals/Common/Components/AnalyticsUnavailableMessage";
import * as React from "react";
import { DetailsWidgetTreeProvider } from "Test/Analytics/Widgets/DetailsWidget/DetailsWidgetTreeProvider";
import * as Resources from "Test/Analytics/Widgets/DetailsWidget/Resources";
import * as UrlCreator from "Test/ManualTestReports/TestReportsCommon/UrlCreator";
import { IVssComponentContext } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Tree, ITreeColumn, ITreeRow } from "VSSUI/TreeEx";
import { ITreeItemEx } from "VSSUI/Utilities/TreeItemProvider";
import { IBehavior } from "VSSUI/Utilities/Behavior";
import { ITable } from "VSSUI/Table";
import { IDetailsWidgetItem } from "Test/Analytics/Widgets/DetailsWidget/Contracts";
import { ObservableValue } from "VSS/Core/Observable";
import { ITestProgressReportWidgetProps } from "Test/ManualTestReports/TestReportsCommon/ITestProgressReportWidgetProps";
import { BaseWidgetComponent } from "Test/ManualTestReports/TestReportsCommon/BaseWidgetComponent";
import { ITestProgressReportFilterService, TestProgressFilterServiceEvents } from "Test/ManualTestReports/TestReportsCommon/TestProgressReportFilterService";
import { IFilterData, ITestManagementSettings } from "Test/ManualTestReports/TestReportsCommon/Contracts";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssContributionService } from "VSS/Platform/Contribution";
import * as TelemetryService from "Test/ManualTestReports/TestReportsCommon/TelemetryService";
import * as TelemetryConstants from "Test/ManualTestReports/TestReportsCommon/TelemetryConstants";

export interface IDetailsWidgetProperties extends ITestProgressReportWidgetProps { }

export interface IDetailsWidgetState {
    loading: boolean;
    errorText?: string;
}

export class DetailsWidget extends BaseWidgetComponent<IDetailsWidgetProperties, IDetailsWidgetState, IVssComponentContext> {

    private _treeItemProvider: DetailsWidgetTreeProvider;
    private _initializePromise: Promise<any>;
    private _testProgressFilterService: ITestProgressReportFilterService;

    private readonly c_testPlanSettingsDataProviderId = "ms.vss-test-analytics-web.testplan-settings-provider";

    constructor(props: IDetailsWidgetProperties, context: IVssComponentContext) {
        super(props, context);

        this._treeItemProvider = new DetailsWidgetTreeProvider(context.pageContext);
        const initializePromise = this._treeItemProvider.getRootLevelItems(undefined, undefined, this._getLastSelectedPlanId(context));
        this._bindToInitializationPromise(initializePromise);
        this.state = { loading: true } as IDetailsWidgetState;
        this._testProgressFilterService = context.pageContext.getService<ITestProgressReportFilterService>("ITestProgressReportFilterService");
    }

    public componentWillMount() {
        this._testProgressFilterService.subscribe(this._onFilterUpdated);
    }

    public async componentDidMount(): Promise<any> {
        await this._initializePromise;
    }

    public componentWillUnmount() {
        this._testProgressFilterService.unsubscribe(this._onFilterUpdated);
    }

    protected isLoading(): boolean {
        return this.state.loading;
    }

    private _onFilterUpdated = (filterData: IFilterData) => {
        this.setState({
            loading: true
        });

        this._treeItemProvider.getRootLevelItems(filterData).then(() => {
            this._onGetRootLevelItemsPromiseSuccess();
        }, (e: Error) => {
            this._onGetRootLevelItemsPromiseError(e);
        });
    }

    private _publishWidgetLoadedEvent() {
        TelemetryService.publishEvent(
            this.context.pageContext, 
            TelemetryConstants.Feature_ProgressReport, 
            { 
                errorText: this.state.errorText,
                event: TelemetryConstants.Event_DetailsWidgetLoaded
            });
    }

    private _getLastSelectedPlanId(context: IVssComponentContext): number | undefined {
        const contributionService = context.pageContext.getService<IVssContributionService>("IVssContributionService");
        let testManagementSettingsData: ITestManagementSettings = contributionService.getData<ITestManagementSettings>(this.c_testPlanSettingsDataProviderId)!;

        return testManagementSettingsData ? testManagementSettingsData.selectedPlanId : undefined;
    }

    private _bindToInitializationPromise(initializePromise: Promise<any>) {
        this._initializePromise = initializePromise;
        initializePromise.then(
            () => {
                this._onGetRootLevelItemsPromiseSuccess();
            },
            (e: Error) => {
                this._onGetRootLevelItemsPromiseError(e);
            }
        );
    }

    private _onActivateRow = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>, row: ITreeRow<ObservableValue<IDetailsWidgetItem>>) => {
        const pageContext = this.context.pageContext;

        const detailWidgetItem = row.data.underlyingItem.data.value || null;
        if (detailWidgetItem !== null) {
            TelemetryService.publishEvent(
                pageContext,
                TelemetryConstants.Feature_ProgressReport,
                {
                    planId: detailWidgetItem.planId,
                    suiteId: detailWidgetItem.id,
                    event: TelemetryConstants.Event_DetailsWidgetOpenSuite
                }
            );
            const link = UrlCreator.getSuiteUrl(detailWidgetItem.planId, detailWidgetItem.id, pageContext);
            window.open(link, '_blank');
        }
    };

    private _onToggle = (event: any, treeItem: ITreeItemEx<ObservableValue<IDetailsWidgetItem>>) => {
        let underlyingItem = treeItem.underlyingItem;
        this._treeItemProvider.toggle(underlyingItem);
        if (underlyingItem.expanded) {
            TelemetryService.publishEvent(
                this.context.pageContext,
                TelemetryConstants.Feature_ProgressReport,
                {
                    planId: underlyingItem.data.value.planId,
                    suiteId: underlyingItem.data.value.id,
                    event: TelemetryConstants.Event_DetailsWidgetTreeExpand
                });
        }
    }

    private _onGetRootLevelItemsPromiseSuccess() {
        this.setState({
            errorText: undefined,
            loading: false
        });
        this._publishWidgetLoadedEvent();
    }

    private _onGetRootLevelItemsPromiseError(e: Error) {
        this.setState({
            loading: false,
            errorText: e.message
        });
        this._publishWidgetLoadedEvent();
    }

    public render() {
        this.logRenderingStart();
        let elementToDisplay = (): JSX.Element => {
            if (this.state.loading === true) {
                return <Spinner className="details-widget-spinner flex-grow flex-column" size={SpinnerSize.large} />;
            } else {
                return <AnalyticsUnavailableMessage suggestion={this.state.errorText} />;
            }
        };

        if (this.state.loading || this.state.errorText) {
            return (
                <Card className="details-widget-container" titleProps={{ text: Resources.DetailsTitle }}>
                    {elementToDisplay()}
                </Card>
            );
        }

        let columnValues: ITreeColumn<ObservableValue<IDetailsWidgetItem>>[] = [];
        this._treeItemProvider.columns.forEach(column => {
            columnValues.push(column.value);
        });

        return (
            <Card className="details-widget-container" titleProps={{ text: Resources.DetailsTitle, className: "title-s" }}>
                <Tree
                    behaviors={[this._treeItemProvider.sorting as IBehavior<any, ITable<IDetailsWidgetItem>>]}
                    columns={columnValues}
                    itemProvider={this._treeItemProvider}
                    onActivate={this._onActivateRow}
                    onToggle={this._onToggle}
                    singleClickActivation={false}
                />
            </Card>
        );
    }
}
